'use client';

import * as React from 'react';
import { ChevronDown, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface CreatableComboboxProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  className?: string;
  emptyText?: string;
  createHint?: string;
}

function normalizeValue(value: string): string {
  return value.trim().toUpperCase();
}

export function CreatableCombobox({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  id,
  'aria-label': ariaLabel,
  className,
  emptyText = 'No options',
  createHint = 'Press Enter to add',
}: CreatableComboboxProps): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [sessionOptions, setSessionOptions] = React.useState<string[]>([]);
  const [search, setSearch] = React.useState('');

  const allOptions = React.useMemo(() => {
    const combined = [...options, ...sessionOptions];
    const seen = new Set<string>();
    return combined.filter((s) => {
      const u = s.toUpperCase();
      if (seen.has(u)) return false;
      seen.add(u);
      return true;
    }).sort((a, b) => a.localeCompare(b));
  }, [options, sessionOptions]);

  const handleSelect = React.useCallback(
    (selected: string) => {
      const normalized = normalizeValue(selected);
      onChange(normalized);
      setOpen(false);
      setSearch('');
    },
    [onChange],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') return;
      const raw = search.trim();
      if (!raw) return;
      const normalized = normalizeValue(raw);
      const exists = allOptions.some(
        (o) => o.toUpperCase() === normalized,
      );
      if (!exists) {
        setSessionOptions((prev) =>
          prev.some((s) => s.toUpperCase() === normalized)
            ? prev
            : [...prev, normalized],
        );
        onChange(normalized);
        setOpen(false);
        setSearch('');
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [search, allOptions, onChange],
  );

  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onChange('');
      setOpen(false);
    },
    [onChange],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          aria-label={ariaLabel}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal h-10',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate">
            {value || placeholder}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {value && (
              <span
                role="button"
                tabIndex={-1}
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClear(e as unknown as React.MouseEvent);
                  }
                }}
                className="rounded p-0.5 hover:bg-muted"
                aria-label="Clear"
              >
                <X className="h-4 w-4" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command
          filter={(value, search) => {
            if (!search?.trim()) return 1;
            return value.toUpperCase().includes(search.trim().toUpperCase())
              ? 1
              : 0;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setSearch('');
            }
          }}
        >
          <CommandInput
            placeholder={placeholder}
            value={search}
            onValueChange={setSearch}
            onKeyDown={handleKeyDown}
          />
          <CommandList>
            <CommandEmpty>
              {search.trim() ? createHint : emptyText}
            </CommandEmpty>
            <CommandGroup>
              {allOptions.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={() => handleSelect(opt)}
                >
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
